import { useEffect, useRef, useState } from "react";
import { FullscreenLogoIntro } from "./FullscreenLogoIntro";
import { MatsushimaAutoLogo } from "./MatsushimaAutoLogo";

type HeaderProps = {
  isHome?: boolean;
};

const SMARTPHONE_LOGO_INTRO_QUERY = "(max-width: 767px)";

function shouldShowLogoIntro(isHome: boolean) {
  return (
    isHome &&
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(SMARTPHONE_LOGO_INTRO_QUERY).matches
  );
}

export function Header({ isHome = false }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoIntroVisible, setIsLogoIntroVisible] = useState(() =>
    shouldShowLogoIntro(isHome),
  );
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const homePrefix = isHome ? "" : "/";

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    if (
      !isLogoIntroVisible ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const smartphoneViewport = window.matchMedia(
      SMARTPHONE_LOGO_INTRO_QUERY,
    );
    const hideIntroOutsideSmartphone = () => {
      if (!smartphoneViewport.matches) {
        setIsLogoIntroVisible(false);
      }
    };

    hideIntroOutsideSmartphone();
    smartphoneViewport.addEventListener(
      "change",
      hideIntroOutsideSmartphone,
    );

    return () => {
      smartphoneViewport.removeEventListener(
        "change",
        hideIntroOutsideSmartphone,
      );
    };
  }, [isLogoIntroVisible]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMenuOpen]);

  return (
    <header className="site-header" data-review-region="site-header">
      <div className="site-header__inner">
        <a
          className="wordmark"
          href="/"
          aria-label="松島オート ホーム"
          data-testid="brand-home-link"
        >
          {isLogoIntroVisible ? null : <MatsushimaAutoLogo />}
        </a>

        <button
          ref={menuButtonRef}
          className="menu-button"
          type="button"
          aria-controls="primary-navigation"
          aria-expanded={isMenuOpen}
          aria-haspopup="true"
          data-testid="menu-button"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span className="menu-button__icon" aria-hidden="true">
            <span className="menu-button__line" />
            <span className="menu-button__line" />
            <span className="menu-button__line" />
          </span>
          <span className="sr-only">
            {isMenuOpen ? "メニューを閉じる" : "メニューを開く"}
          </span>
        </button>

        <nav
          id="primary-navigation"
          className={`primary-navigation ${isMenuOpen ? "is-open" : ""}`}
          aria-label="メインナビゲーション"
        >
          <a href={`${homePrefix}#vehicle-fit`} onClick={closeMenu}>
            対応できる車
          </a>
          <a href={`${homePrefix}#flow`} onClick={closeMenu}>
            買取の流れ
          </a>
          <a href={`${homePrefix}#faq`} onClick={closeMenu}>
            よくある質問
          </a>
          <a href={`${homePrefix}#company`} onClick={closeMenu}>
            事業者情報
          </a>
        </nav>
      </div>
      {isLogoIntroVisible ? (
        <FullscreenLogoIntro
          onComplete={() => setIsLogoIntroVisible(false)}
        />
      ) : null}
    </header>
  );
}
